import { Grid, Select, Spin } from "antd";
import styles from "./styles.module.scss";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import { useEffect, useState } from "react";
import { ConditionalRenderer } from "components";
import { UseApiCallForOldDashboardData } from "hooks/use-api-call-for-old-dashboard";
import { convertAPIObjectToArray } from "./heplers";
import { UseLazyApiCallOldDashboard } from "hooks/use-lazy-api-call-old-dashboard";
import { timespanOptions } from "./constants";
import { ReactComponent as DropdownArrow } from "icons/dropdownArrow.svg";
import { useRecoilState } from "recoil";
import { persistedUserOrStrikerId } from "store/atom";

const { useBreakpoint } = Grid;

export const TotalCaptures = ({ userID }) => {
  const screens = useBreakpoint();
  const [chartType, setChartType] = useState("line");
  const [graphTimeSpan, setGraphTimespan] = useState("2025");

  const handleChange = (value) => {
    setGraphTimespan(value);
  };
  const handleChartTypeClick = (value) => {
    setChartType(value);
  };

  const [
    getTotalCaptures,
    { data: totalCaptures, isLoading: totalCapturesLoading, error, refetch },
  ] = UseLazyApiCallOldDashboard({
    url: "user_video_graph",
    method: "POST",
    skip: !userID,
  });

  async function getData(graphTimeSpan) {
    const payload = {
      userID: userID,
      timespan: graphTimeSpan,
    };
    try {
      await getTotalCaptures({
        params: payload,
      });
    } catch (error) {
      console.log({ error });
    }
  }

  useEffect(() => {
    if (graphTimeSpan && userID) {
      getData(graphTimeSpan);
    }
  }, [graphTimeSpan, userID]);

  const formattedGraphData = convertAPIObjectToArray(
    totalCaptures,
    graphTimeSpan,
  );

  return (
    <div className={styles.totalCaptures}>
      <div className={styles.header}>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div className={styles.heading}>Total Captures</div>
          <ChartToggleButtons
            onChartTypeClick={handleChartTypeClick}
            chartType={chartType}
          />
        </div>

        <Select
          defaultValue="2025"
          style={{ width: 120 }}
          onChange={handleChange}
          options={timespanOptions}
          suffixIcon={<DropdownArrow />}
        />
      </div>

      <div
        style={{
          height: "calc(100% - 50px)",
          width: "100%",
        }}
      >
        <ConditionalRenderer condition={chartType === "line"}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={formattedGraphData}
              margin={{ top: 25, right: 10, left: -15 }}
            >
              {/* Gradient and Shadow Filter */}
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <defs>
                {/* Gradient Definition */}
                <linearGradient
                  id="lineGradient"
                  x1="100%"
                  y1="0%"
                  x2="0%"
                  y2="0%"
                >
                  <stop offset="0.04%" stopColor="#D3EBF7" />
                  <stop offset="17.76%" stopColor="#34B3F1" />
                  <stop offset="36.56%" stopColor="#003488" />
                  <stop offset="52.1%" stopColor="#003488" />
                  <stop offset="73.88%" stopColor="#34B3F1" />
                  <stop offset="97.57%" stopColor="#D5EDF8" />
                </linearGradient>

                {/* Drop Shadow Filter */}
                <filter
                  id="shadow"
                  x="-30%"
                  y="-30%"
                  width="160%"
                  height="160%"
                >
                  <feDropShadow
                    dx="0"
                    dy="5"
                    stdDeviation="6"
                    floodColor="#079BE2"
                    floodOpacity="0.9"
                  />
                </filter>
              </defs>

              <XAxis
                dataKey="month"
                style={{
                  fontSize: screens.xl ? 12 : 8,
                }}
              />
              <YAxis
                style={{
                  fontSize: screens.xl ? 12 : 8,
                }}
              />
              <Tooltip />

              <Line
                type="monotone"
                dataKey="captures"
                stroke="url(#lineGradient)"
                strokeWidth={4.1}
                filter="url(#shadow)"
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ConditionalRenderer>
        <ConditionalRenderer condition={chartType === "bar"}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              width={217}
              height={53}
              data={formattedGraphData}
              margin={{ top: 25, right: 10, left: -15 }}
            >
              <defs>
                <linearGradient id="colorUv" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#11A9FF" stopOpacity={1} />
                  <stop offset="100%" stopColor="#016AB3" stopOpacity={1} />
                </linearGradient>
              </defs>
              <Bar dataKey="captures" fill="url(#colorUv)" />
              <CartesianGrid strokeDasharray="3 3" vertical={false} />

              <XAxis
                dataKey="month"
                style={{
                  fontSize: screens.xl ? 12 : 8,
                }}
              />
              <YAxis
                style={{
                  fontSize: screens.xl ? 12 : 8,
                }}
              />
              <Tooltip />
            </BarChart>
          </ResponsiveContainer>
        </ConditionalRenderer>
      </div>
    </div>
  );
};

const ChartToggleButtons = ({ onChartTypeClick, chartType }) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
      }}
    >
      <button
        className={
          chartType === "line"
            ? styles.toggleButtonsActive
            : styles.toggleButtonsInactive
        }
        onClick={() => onChartTypeClick("line")}
      >
        <img
          src="/assets/icons/lineChart.svg"
          alt="Icon"
          style={{ marginRight: "5px" }}
        />
        Line Chart
      </button>
      <button
        className={
          chartType === "bar"
            ? styles.toggleButtonsActive
            : styles.toggleButtonsInactive
        }
        onClick={() => onChartTypeClick("bar")}
      >
        <img
          src="/assets/icons/barChart.svg"
          alt="Icon"
          style={{ marginRight: "5px" }}
        />
        Stacked Bar Chart
      </button>
    </div>
  );
};
